'use client';
import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, useSearchParams } from 'next/navigation';
import OtpInput from './OtpInput';

type AuthMode = 'choose' | 'otp-phone' | 'otp-verify';

export default function AuthScreen() {
  const { signInWithGoogle, signInWithPhone, verifyOtp } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [mode, setMode] = useState<AuthMode>('choose');
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [googleError, setGoogleError] = useState<string | null>(null);

  // Clear stale ?error= URL param on mount so a previous callback failure
  // does not permanently show an error or block a fresh login attempt.
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      // Replace current URL without the error param — no page reload
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());

      // Show a one-time informational message
      if (errorParam === 'auth_callback_failed') {
        setGoogleError('Previous sign-in attempt failed. Please try again.');
      }
    }
  }, [searchParams]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneError('');
    if (!phone) { setPhoneError('Mobile number is required'); return; }
    if (!/^[6-9]\d{9}$/.test(phone)) { setPhoneError('Enter a valid 10-digit Indian mobile number'); return; }
    setLoading(true);
    try {
      await signInWithPhone(phone);
      setMode('otp-verify');
      toast.success(`OTP sent to +91 ${phone}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (otp: string) => {
    setLoading(true);
    try {
      const data = await verifyOtp(phone, otp);
      if (data?.user) {
        toast.success('Verified! Signing you in...');
        router.push('/auth/role-redirect');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    // DEBUG: confirm click handler fires
    console.log('[AuthScreen] google_login_clicked');
    setGoogleError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
      // If we reach here without a redirect, OAuth URL was not opened
      console.warn('[AuthScreen] signInWithGoogle returned without redirecting');
    } catch (err: any) {
      const msg = err?.message || 'Google sign-in failed. Please try again.';
      console.error('[AuthScreen] signInWithGoogle error:', msg);
      // Surface error visibly on screen — do NOT only use toast
      setGoogleError(`Google sign-in failed: ${msg}`);
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="flex flex-col items-center mb-10">
          <div className="flex items-center gap-3 mb-4">
            <AppLogo size={48} />
            <span className="font-extrabold text-3xl text-primary tracking-tight">Raahi</span>
          </div>
          <p className="text-base font-semibold text-foreground text-center">Shared rides. Simple journeys.</p>
          <p className="text-sm text-muted-foreground text-center mt-1 max-w-xs">
            Join the queue for your route. Get matched automatically.
          </p>
        </div>

        {/* OTP Verify */}
        {mode === 'otp-verify' && (
          <div className="card-base p-6">
            <h2 className="text-lg font-bold text-foreground mb-1">Enter OTP</h2>
            <p className="text-sm text-muted-foreground mb-5">We sent a 6-digit code to +91 {phone}</p>
            <OtpInput onComplete={handleVerifyOtp} loading={loading} onBack={() => setMode('otp-phone')} />
          </div>
        )}

        {/* Phone entry */}
        {mode === 'otp-phone' && (
          <div className="card-base p-6">
            <h2 className="text-lg font-bold text-foreground mb-1">Enter your mobile number</h2>
            <p className="text-sm text-muted-foreground mb-5">We'll send you a one-time password</p>
            <form onSubmit={handleSendOtp} className="flex flex-col gap-4">
              <div>
                <div className="flex">
                  <span className="flex items-center px-3 bg-muted border border-r-0 border-border rounded-l-xl text-sm font-semibold text-muted-foreground">+91</span>
                  <input
                    type="tel"
                    className="input-field rounded-l-none flex-1"
                    placeholder="10-digit mobile number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    autoComplete="tel"
                    inputMode="numeric"
                  />
                </div>
                {phoneError && <p className="text-xs text-danger mt-1">{phoneError}</p>}
              </div>
              <button type="submit" className="btn-primary w-full justify-center" disabled={loading}>
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Sending OTP...
                  </span>
                ) : (
                  <>Send OTP <ArrowRight size={16} /></>
                )}
              </button>
              <button type="button" onClick={() => setMode('choose')} className="text-sm text-muted-foreground hover:text-foreground text-center transition-colors">
                ← Back
              </button>
            </form>
          </div>
        )}

        {/* Main choose screen */}
        {mode === 'choose' && (
          <div className="flex flex-col gap-3">
            {/* Visible on-screen error — never silently swallow OAuth errors */}
            {googleError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                {googleError}
              </div>
            )}

            <button
              onClick={handleGoogleSignIn}
              className="btn-secondary w-full gap-3 py-3.5 justify-center"
              disabled={loading}
              type="button"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-muted-foreground/40 border-t-foreground rounded-full animate-spin" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              )}
              Continue with Google
            </button>

            <button
              onClick={() => setMode('otp-phone')}
              className="btn-primary w-full gap-3 py-3.5 justify-center"
              disabled={loading}
              type="button"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              Continue with Mobile OTP
            </button>
          </div>
        )}
      </div>
    </div>
  );
}